import { useEffect, useState } from "react";
import {
  getPreferences,
  listMonitorRuns,
  listTickets,
  savePreferences,
  type MonitorRun,
} from "../lib/api";

const NAVY = "#0b1f4a";
const ACCENT_100 = "#fff3e4";
const ACCENT_800 = "#5a3b0a";
const ACCENT_900 = "#3a270d";

type Freq = "weekly" | "daily";

// Här låg fyra påhittade bevakningskörningar ("Veckoscan · 9 källor ·
// 1 larm (MrKoll återkommen)") som visades likadant för alla inloggade.
// Vi har ingen separat körningslogg — listan byggs av ärendenas tidslinjer.

/**
 * Bevaka — weekly/daily toggle, timeline, reappeared alert.
 * Mock only. Route suggestion: /monitor or /bevaka
 */
export default function Monitor() {
  const [freq, setFreq] = useState<Freq>("weekly");
  const [mail, setMail] = useState(true);
  const [runs, setRuns] = useState<MonitorRun[]>([]);
  const [monitored, setMonitored] = useState(false);
  const [saving, setSaving] = useState(false);
  const [reappeared, setReappeared] = useState<
    { id: string; caseId: string; sourceName?: string }[]
  >([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const [runsRes, prefs, ticketsRes] = await Promise.all([
          listMonitorRuns(),
          getPreferences(),
          listTickets(),
        ]);
        if (!alive) return;
        setRuns(runsRes.data ?? []);
        setMonitored(runsRes.meta?.monitored ?? false);
        setFreq(prefs.data.monitorFreq);
        setMail(prefs.data.notifyEmail);
        setReappeared(
          (ticketsRes.data ?? [])
            .filter((t) => t.status === "REAPPEARED")
            .map((t) => ({ id: t.id, caseId: t.caseId, sourceName: t.sourceName })),
        );
      } catch {
        /* utloggad eller nätfel — sidan visar tomt läge */
      } finally {
        if (alive) setLoading(false);
      }
    })();
    return () => {
      alive = false;
    };
  }, []);

  /** Inställningarna var tidigare bara React-state och försvann vid
   *  sidladdning. Nu sparas de på kontot, och frekvensen styr när cron
   *  plockar upp subjekten. */
  const persist = async (patch: {
    monitorFreq?: "weekly" | "daily";
    notifyEmail?: boolean;
  }) => {
    setSaving(true);
    try {
      const res = await savePreferences(patch);
      setFreq(res.data.monitorFreq);
      setMail(res.data.notifyEmail);
      showToast("Sparat");
    } catch (e) {
      const msg = e instanceof Error ? e.message : "";
      showToast(
        msg === "payment_required"
          ? "Daglig bevakning ingår i Skydd och Familj."
          : msg === "unauthorized"
            ? "Du är utloggad."
            : "Kunde inte spara.",
      );
      // Rulla tillbaka så knappen inte visar ett val som inte gäller.
      try {
        const cur = await getPreferences();
        setFreq(cur.data.monitorFreq);
        setMail(cur.data.notifyEmail);
      } catch {
        /* strunt i det */
      }
    } finally {
      setSaving(false);
    }
  };

  const day = (ms: number) => (ms > 0 ? new Date(ms).toISOString().slice(0, 10) : "—");
  const [toast, setToast] = useState<string | null>(null);

  const showToast = (msg: string) => {
    setToast(msg);
    window.setTimeout(() => setToast(null), 2400);
  };

  const seg = (on: boolean) =>
    ({
      padding: "8px 16px",
      border: 0,
      background: on ? ACCENT_100 : "transparent",
      fontSize: 13,
      cursor: "pointer" as const,
      color: on ? ACCENT_900 : "#7d7979",
      fontWeight: on ? 600 : 400,
    }) as const;

  const toggleStyle = (on: boolean) =>
    ({
      width: 40,
      height: 22,
      borderRadius: 99,
      border: on ? `1px solid ${NAVY}` : "1px solid #d7d3d3",
      background: on ? NAVY : "#eae7e7",
      cursor: "pointer",
      position: "relative" as const,
      padding: 0,
    }) as const;

  const knobStyle = (on: boolean) =>
    ({
      position: "absolute" as const,
      top: 2,
      left: on ? 20 : 2,
      width: 16,
      height: 16,
      borderRadius: "50%",
      background: "#fff",
      transition: "left 0.15s",
    }) as const;

  return (
    <div data-page="monitor" style={{ fontFamily: '"Lora", Georgia, serif', color: "#201f1d" }}>
      <h1
        style={{
          fontFamily: '"Cormorant Garamond", Georgia, serif',
          fontWeight: 400,
          fontSize: 34,
          margin: 0,
          color: NAVY,
        }}
      >
        Bevaka
      </h1>
      <p
        style={{
          fontSize: 14.5,
          color: "#7d7979",
          margin: "10px 0 0",
          maxWidth: "60ch",
          lineHeight: 1.65,
        }}
      >
        Vi söker igenom alla källor igen enligt ditt schema. Återkommer uppgifter köas en ny
        borttagning automatiskt.
      </p>

      <div
        style={{
          display: "grid",
          gridTemplateColumns: "minmax(0, 7fr) minmax(240px, 5fr)",
          gap: 20,
          marginTop: 24,
          alignItems: "start",
        }}
      >
        <div
          style={{
            background: "#fff",
            border: "1px solid color-mix(in srgb, #201f1d 16%, transparent)",
            borderRadius: 7,
            padding: "8px 24px",
          }}
        >
          <div
            style={{
              display: "flex",
              alignItems: "center",
              gap: 14,
              padding: "16px 0",
              borderBottom: "1px solid color-mix(in srgb, #201f1d 16%, transparent)",
              flexWrap: "wrap",
            }}
          >
            <span style={{ fontSize: 14, fontWeight: 600, color: NAVY, flex: 1 }}>Frekvens</span>
            <div
              style={{
                display: "flex",
                border: "1px solid color-mix(in srgb, #201f1d 16%, transparent)",
                borderRadius: 4,
                overflow: "hidden",
              }}
            >
              <button
                type="button"
                disabled={saving}
                onClick={() => void persist({ monitorFreq: "weekly" })}
                style={seg(freq === "weekly")}
              >
                Veckovis
              </button>
              <button
                type="button"
                disabled={saving}
                onClick={() => void persist({ monitorFreq: "daily" })}
                style={seg(freq === "daily")}
              >
                Dagligen
              </button>
            </div>
            {freq === "daily" && (
              <span
                style={{
                  fontSize: 10.5,
                  letterSpacing: "0.06em",
                  textTransform: "uppercase",
                  padding: "2px 9px",
                  borderRadius: 99,
                  background: ACCENT_100,
                  color: ACCENT_800,
                }}
              >
                Premium
              </span>
            )}
          </div>

          <div
            style={{
              borderLeft: "1px solid color-mix(in srgb, #201f1d 16%, transparent)",
              margin: "18px 0 18px 6px",
              paddingLeft: 22,
              display: "flex",
              flexDirection: "column",
              gap: 18,
            }}
          >
            {loading && (
              <div style={{ fontSize: 14, color: "#9b9797" }}>Hämtar…</div>
            )}
            {!loading && runs.length === 0 && (
              <div style={{ fontSize: 14, color: "#9b9797" }}>
                {monitored
                  ? "Inga körningar ännu — första bevakningen körs inom kort."
                  : "Bevakning ingår i Skydd och Familj. Med gratisnivån scannar du själv när du vill."}
              </div>
            )}
            {runs.map((m) => (
              <div key={m.id} style={{ position: "relative" }}>
                <span
                  style={{
                    position: "absolute",
                    left: -27,
                    top: 5,
                    width: 9,
                    height: 9,
                    borderRadius: "50%",
                    background: m.alerts > 0 ? "#B42318" : "#0F7B4B",
                    border: "2px solid #f3f2f2",
                  }}
                />
                <div style={{ fontSize: 12.5, color: "#9b9797", fontFeatureSettings: '"tnum" 1' }}>
                  {day(m.startedAt)}
                </div>
                <div style={{ fontSize: 14, color: "#201f1d", marginTop: 2 }}>
                  {m.status === "error"
                    ? "Körningen misslyckades — försöker igen"
                    : `${m.sources} källor kontrollerade${m.alerts > 0 ? ` · ${m.alerts} larm` : " · inga larm"}`}
                </div>
              </div>
            ))}
          </div>
        </div>

        <div style={{ display: "flex", flexDirection: "column", gap: 20 }}>
          {/* Larmkortet var tidigare fast: "Dina uppgifter dök upp igen på
              MrKoll … ärende GE-4201", visat för alla. Nu visas det bara för
              ärenden som faktiskt har status REAPPEARED. */}
          {reappeared.map((t) => (
            <div
              key={t.id}
              style={{
                background: "#fff",
                border: "1px solid #C47F00",
                borderRadius: 7,
                padding: "20px 24px",
              }}
              data-testid="reappeared-alert"
            >
              <span
                style={{
                  fontSize: 11,
                  letterSpacing: "0.05em",
                  padding: "3px 10px",
                  borderRadius: 99,
                  border: "1px solid #B42318",
                  color: "#B42318",
                }}
              >
                Återkommen
              </span>
              <div style={{ fontSize: 14.5, color: "#201f1d", marginTop: 12, lineHeight: 1.6 }}>
                Dina uppgifter dök upp igen på{" "}
                <strong style={{ fontWeight: 600 }}>{t.sourceName ?? "en källa"}</strong>.
              </div>
              <div style={{ fontSize: 13, color: "#7d7979", marginTop: 6 }}>
                Ärende {t.caseId}
              </div>
            </div>
          ))}

          <div
            style={{
              background: "#fff",
              border: "1px solid color-mix(in srgb, #201f1d 16%, transparent)",
              borderRadius: 7,
              padding: "8px 24px",
            }}
          >
            <div
              style={{
                display: "flex",
                alignItems: "center",
                padding: "15px 0",
                borderBottom: "1px solid color-mix(in srgb, #201f1d 16%, transparent)",
                fontSize: 14,
              }}
            >
              <span style={{ flex: 1 }}>E-postnotiser</span>
              <button
                type="button"
                aria-pressed={mail}
                aria-label="E-postnotiser"
                disabled={saving}
                onClick={() => void persist({ notifyEmail: !mail })}
                style={toggleStyle(mail)}
              >
                <span style={knobStyle(mail)} />
              </button>
            </div>
            {/* Push-reglaget är borttaget: ingen push-implementation finns,
                så valet gick att slå på utan att kunna leda till en notis. */}
          </div>
        </div>
      </div>

      {toast && (
        <div
          style={{
            position: "fixed",
            bottom: 28,
            left: "50%",
            transform: "translateX(-50%)",
            background: NAVY,
            color: "#fff",
            borderRadius: 4,
            padding: "12px 22px",
            fontSize: 14,
            zIndex: 80,
          }}
        >
          {toast}
        </div>
      )}
    </div>
  );
}
